import { useCallback } from "react";
import {
  DataSnapshot,
  off,
  onValue,
  ref,
  remove,
  set,
} from "firebase/database";
import useSystemClock from "modules/system/hooks/useSystemClock";
import { useSelector } from "react-redux";
import { database, serverValue } from "services/firebaseService/firebaseConfig";
import { selectCurrentEventId, selectHasJoinedEvent } from "../selectors";

const getExtendEventMessageDocRef = (eventId: string) =>
  ref(database, `Event/${eventId}/ExtendMessage`);

type UseEventMessages = {
  subscribeToCloseExtendEventMessage: (onClose: () => void) => void;
  unsubscribeFromCloseExtendEventMessage: () => void;
  closeAllExtendNotifications: () => void;
};

const useEventMessages = (): UseEventMessages => {
  const currentEventId = useSelector(selectCurrentEventId);
  const hasJoinedEvent = useSelector(selectHasJoinedEvent);
  const { deviation } = useSystemClock();

  const closeAllExtendNotifications = useCallback(() => {
    if (!currentEventId) return;

    set(getExtendEventMessageDocRef(currentEventId), {
      closedAt: serverValue.TIMESTAMP,
    });
  }, [currentEventId]);

  const subscribeToCloseExtendEventMessage = useCallback(
    (onClose: () => void) => {
      if (!hasJoinedEvent || !currentEventId) return;
      const dbRef = getExtendEventMessageDocRef(currentEventId);

      off(dbRef);
      onValue(dbRef, (snap: DataSnapshot) => {
        const data = snap.val();

        if (!data) {
          return;
        }
        const { closedAt } = data;
        const checkPeriod = 10 * 60 * 60 * 1000; // 10 min

        // check if popup closed during last 10 min and it's not an old value
        if (Date.now() - closedAt - deviation > checkPeriod) {
          remove(dbRef);

          return;
        }

        onClose();
      });
    },
    [currentEventId, deviation, hasJoinedEvent],
  );

  const unsubscribeFromCloseExtendEventMessage = useCallback(() => {
    if (!hasJoinedEvent || !currentEventId) return;
    off(getExtendEventMessageDocRef(currentEventId));
  }, [currentEventId, hasJoinedEvent]);

  return {
    subscribeToCloseExtendEventMessage,
    unsubscribeFromCloseExtendEventMessage,
    closeAllExtendNotifications,
  };
};

export default useEventMessages;
